import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';
import { notifications } from '@mantine/notifications';

// Types
export type OnlineStatus = 'online' | 'offline' | 'unknown';

export interface DeviceStatus {
  id: string;
  deviceId: string;
  status: OnlineStatus;
  statusLabel: string;
  statusColor: string;
  lastCheckedAt: string | null;
  lastOnlineAt: string | null;
  latencyMs: number | null;
  checkMethod: string | null;
  consecutiveFailures: number;
  winrmAvailable: boolean;
  updatedAt: string;
  // Device details (included in list response)
  hostname: string;
  ipAddress: string | null;
  site: string | null;
  siteId: string | null;
  room: string | null;
  roomId: string | null;
  deviceType: string | null;
  primaryMacAddress: string | null;
}

export interface StatusSummary {
  online: number;
  offline: number;
  unknown: number;
  total: number;
}

export interface PowerActionLog {
  id: string;
  deviceId: string;
  deviceName: string;
  actionType: string;
  actionTypeLabel: string;
  status: string;
  statusLabel: string;
  initiatedBy: string;
  targetMacAddress: string | null;
  broadcastAddress: string | null;
  errorMessage: string | null;
  initiatedAt: string;
  completedAt: string | null;
}

interface StatusListResponse {
  data: DeviceStatus[];
  total: number;
}

interface StatusResponse {
  data: DeviceStatus;
}

interface StatusSummaryResponse {
  data: StatusSummary;
}

interface PowerLogsResponse {
  data: PowerActionLog[];
  total: number;
}

interface WolResponse {
  data: PowerActionLog;
  success: boolean;
}

interface WolBulkResponse {
  data: PowerActionLog[];
  summary: {
    total: number;
    success: number;
    failed: number;
  };
}

interface CheckBulkResponse {
  message: string;
  tasksCreated: number;
  requested: number;
}

/**
 * Hook to fetch device status summary (online/offline counts)
 */
export function useStatusSummary() {
  return useQuery({
    queryKey: queryKeys.remote.statusSummary,
    queryFn: async () => {
      const response = await api.get<StatusSummaryResponse>(endpoints.remote.statusSummary);
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

/**
 * Hook to fetch all device statuses
 */
export function useDeviceStatuses() {
  return useQuery({
    queryKey: queryKeys.remote.statusList,
    queryFn: async () => {
      const response = await api.get<StatusListResponse>(endpoints.remote.statusList);
      return response.data;
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook to fetch status for a specific device
 */
export function useDeviceStatus(deviceId: string) {
  return useQuery({
    queryKey: queryKeys.remote.status(deviceId),
    queryFn: async () => {
      const response = await api.get<StatusResponse>(endpoints.remote.status(deviceId));
      return response.data;
    },
    enabled: !!deviceId,
  });
}

/**
 * Hook to trigger status check for a single device
 */
export function useCheckDeviceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deviceId: string) => {
      const response = await api.post<StatusResponse>(endpoints.remote.checkStatus(deviceId));
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate status queries
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.all });

      const statusLabel = data.status === 'online' ? 'Online' :
                          data.status === 'offline' ? 'Offline' : 'Unknown';

      notifications.show({
        title: 'Status Check Complete',
        message: `Device is ${statusLabel}`,
        color: data.status === 'online' ? 'green' : data.status === 'offline' ? 'red' : 'gray',
      });
    },
  });
}

/**
 * Hook to trigger bulk status check via relay agent
 */
export function useCheckDeviceStatusBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deviceIds: string[]) => {
      const response = await api.post<CheckBulkResponse>(endpoints.remote.checkStatusBulk, {
        deviceIds,
      });
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.all });

      notifications.show({
        title: 'Status Check Started',
        message: `Created ${data.tasksCreated} status check tasks. Results will appear shortly.`,
        color: 'blue',
        autoClose: 5000,
      });
    },
  });
}

interface CheckAllResponse {
  message: string;
  tasksCreated: number;
  agentId?: string;
  agentName?: string;
  error?: string;
}

/**
 * Hook to check status for ALL devices in the system via relay agent
 * Creates ping tasks that will be executed by the relay agent
 * Results will update DeviceStatus as tasks complete
 */
export function useCheckStatusAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentId?: string | void) => {
      const body = agentId ? { agent_id: agentId } : undefined;
      const response = await api.post<CheckAllResponse>(endpoints.remote.checkStatusAll, body);
      return response;
    },
    onSuccess: (data) => {
      // Invalidate queries to start polling for updates
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.all });

      if (data.tasksCreated > 0) {
        notifications.show({
          title: 'Status Check Started',
          message: `Created ${data.tasksCreated} status check tasks via ${data.agentName || 'relay agent'}. Results will appear shortly.`,
          color: 'blue',
          autoClose: 5000,
        });
      } else {
        notifications.show({
          title: 'No Tasks Created',
          message: data.message || 'No devices to check',
          color: 'yellow',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Status Check Failed',
        message: error instanceof Error ? error.message : 'Failed to initiate status checks',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to send Wake-on-LAN to a device
 */
export function useWakeOnLan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, initiatedBy = 'ui' }: { deviceId: string; initiatedBy?: string }) => {
      const response = await api.post<WolResponse>(endpoints.remote.wol(deviceId), {
        initiatedBy,
      });
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.powerLogs });

      if (data.success) {
        notifications.show({
          title: 'Wake-on-LAN Sent',
          message: `Magic packet sent to ${data.data.targetMacAddress}`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Wake-on-LAN Failed',
          message: data.data.errorMessage || 'Failed to send magic packet',
          color: 'red',
        });
      }
    },
  });
}

/**
 * Hook to send Wake-on-LAN to multiple devices
 */
export function useWakeOnLanBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceIds, initiatedBy = 'ui' }: { deviceIds: string[]; initiatedBy?: string }) => {
      const response = await api.post<WolBulkResponse>(endpoints.remote.wolBulk, {
        deviceIds,
        initiatedBy,
      });
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.powerLogs });

      notifications.show({
        title: 'Bulk Wake-on-LAN Complete',
        message: `Sent: ${data.summary.success}, Failed: ${data.summary.failed}`,
        color: data.summary.failed > 0 ? 'yellow' : 'green',
      });
    },
  });
}

interface WolRoomResponse {
  data: PowerActionLog[];
  room: {
    id: string;
    name: string;
    code: string;
  };
  summary: {
    total: number;
    success: number;
    failed: number;
  };
}

interface WolGroupResponse {
  data: PowerActionLog[];
  group: {
    id: string;
    name: string;
  };
  summary: {
    total: number;
    success: number;
    failed: number;
  };
}

/**
 * Hook to send Wake-on-LAN to all devices in a room
 */
export function useWakeOnLanRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roomId, initiatedBy = 'ui' }: { roomId: string; initiatedBy?: string }) => {
      const response = await api.post<WolRoomResponse>(endpoints.remote.wolRoom(roomId), {
        initiatedBy,
      });
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.powerLogs });

      notifications.show({
        title: `Wake-on-LAN: ${data.room.name}`,
        message: `Sent: ${data.summary.success}, Failed: ${data.summary.failed}`,
        color: data.summary.failed > 0 ? 'yellow' : 'green',
      });
    },
  });
}

/**
 * Hook to send Wake-on-LAN to all devices in a group
 */
export function useWakeOnLanGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, initiatedBy = 'ui' }: { groupId: string; initiatedBy?: string }) => {
      const response = await api.post<WolGroupResponse>(endpoints.remote.wolGroup(groupId), {
        initiatedBy,
      });
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.powerLogs });

      notifications.show({
        title: `Wake-on-LAN: ${data.group.name}`,
        message: `Sent: ${data.summary.success}, Failed: ${data.summary.failed}`,
        color: data.summary.failed > 0 ? 'yellow' : 'green',
      });
    },
  });
}

/**
 * Hook to fetch recent power action logs
 */
export function usePowerLogs(limit: number = 100) {
  return useQuery({
    queryKey: queryKeys.remote.powerLogs,
    queryFn: async () => {
      const response = await api.get<PowerLogsResponse>(endpoints.remote.powerLogs, {
        params: { limit },
      });
      return response.data;
    },
  });
}

/**
 * Hook to fetch power action logs for a specific device
 */
export function useDevicePowerLogs(deviceId: string, limit: number = 50) {
  return useQuery({
    queryKey: queryKeys.remote.devicePowerLogs(deviceId),
    queryFn: async () => {
      const response = await api.get<PowerLogsResponse>(endpoints.remote.powerLogsDevice(deviceId), {
        params: { limit },
      });
      return response.data;
    },
    enabled: !!deviceId,
  });
}

// ============= CREDENTIALS =============

export interface DeviceCredentials {
  deviceId: string;
  hasCredentials: boolean;
  username: string | null;
}

interface CredentialsResponse {
  deviceId: string;
  hasCredentials: boolean;
  username: string | null;
}

interface SetCredentialsResponse {
  success: boolean;
  deviceId: string;
  username: string;
  message: string;
}

interface ClearCredentialsResponse {
  success: boolean;
  deviceId: string;
  message: string;
}

interface SetCredentialsBulkResponse {
  success: boolean;
  updated: number;
  notFound: string[];
  message: string;
}

/**
 * Hook to check if a device has WinRM credentials configured
 */
export function useDeviceCredentials(deviceId: string) {
  return useQuery({
    queryKey: queryKeys.remote.credentials(deviceId),
    queryFn: async () => {
      const response = await api.get<CredentialsResponse>(endpoints.remote.credentials(deviceId));
      return response;
    },
    enabled: !!deviceId,
  });
}

/**
 * Hook to set WinRM credentials for a device
 */
export function useSetDeviceCredentials() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deviceId,
      username,
      password,
    }: {
      deviceId: string;
      username: string;
      password: string;
    }) => {
      const response = await api.post<SetCredentialsResponse>(
        endpoints.remote.credentials(deviceId),
        { username, password }
      );
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.credentials(data.deviceId) });

      notifications.show({
        title: 'Credentials Saved',
        message: `WinRM credentials set for user: ${data.username}`,
        color: 'green',
      });
    },
  });
}

/**
 * Hook to clear WinRM credentials from a device
 */
export function useClearDeviceCredentials() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deviceId: string) => {
      const response = await api.delete<ClearCredentialsResponse>(
        endpoints.remote.credentials(deviceId)
      );
      return { ...response, deviceId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.remote.credentials(data.deviceId) });

      notifications.show({
        title: 'Credentials Cleared',
        message: 'WinRM credentials removed from device',
        color: 'blue',
      });
    },
  });
}

/**
 * Hook to set WinRM credentials for multiple devices (bulk)
 */
export function useSetCredentialsBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deviceIds,
      username,
      password,
    }: {
      deviceIds: string[];
      username: string;
      password: string;
    }) => {
      const response = await api.post<SetCredentialsBulkResponse>(
        endpoints.remote.credentialsBulk,
        { deviceIds, username, password }
      );
      return response;
    },
    onSuccess: (data) => {
      // Invalidate all credential queries
      queryClient.invalidateQueries({ queryKey: ['remote', 'credentials'] });

      notifications.show({
        title: 'Bulk Credentials Updated',
        message: data.message,
        color: data.notFound.length > 0 ? 'yellow' : 'green',
      });
    },
  });
}
